import React, {useContext, useEffect, useState} from 'react';
import {CircularProgress} from '@material-ui/core';
import {useHistory} from 'react-router-dom';
import LocationInput from '../LocationInput/LocationInput';
import {IPlace} from '../../Foundations/Types/IPlace';
import StartDatePicker from '../Datepicker/StartDatePicker';
import Button from '../../Components/Button';
import {
    checkSearchReferenceStatus,
    createSearchReference,
    fetchResult,
    getHardLimits
} from '../../Foundations/API/SearchApi';
import appContext from '../../Foundations/Contexts/AppContext';
import {formatDate, subtractDays} from '../../Foundations/Utils/DateUtil';
import {ISearchResponse} from '../../Foundations/Types/ISearchResponse';
import MapIcon from '../../Components/Icons/Map';
import SvgIcon from '../../Components/Icons/SvgIcon';
import ArrowDown from '../../Components/Icons/ArrowDown';
import resultsContext from '../../Foundations/Contexts/ResultsContext';
import {findResultInStorage, saveResultsToStorage} from '../../Foundations/Repositories/ResultRepository';
import ConfirmSearchModal from '../../Components/Modals/ConfirmSearchModal';
import SearchState from '../../Foundations/Enum/SearchState';
import {IBaseSearchParam} from '../../Foundations/Types/ISearchParam';
import searchContext from '../../Foundations/Contexts/SearchContext';
import APP_PATH from '../../Foundations/Constants/AppPath';
import {generatePlaceObject} from '../../Foundations/Utils/ResultsUtils';
import searchFilterContext from '../../Foundations/Contexts/SearchFilterContext';
import {parseSearchParametersFromUrl} from '../../Foundations/Utils/QueryParamUtils';
import WarningModal from '../../Components/Modals/WarningModal';
import IHardSearchLimits from '../../Foundations/Types/IHardSearchLimits';

interface IProps {
    className?: string;
    initialDate?: Date;
    lat?: number;
    lng?: number;
    queryParamDetection?: boolean;
}

const RatesSearchForm: React.FC<IProps> = (props) => {
    const today = new Date();
    const history = useHistory();
    const { user, env } = useContext(appContext);
    const { setQueue } = useContext(searchContext);
    const {setMapView, isMapViewOpen, limitedBy} = useContext(resultsContext);
    const {searchParameters} = useContext(searchFilterContext);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [staticLocation] = useState<IPlace | null>(
        typeof props.lng !== 'undefined' && typeof props.lat !== 'undefined'
        ? generatePlaceObject(searchParameters)
        : null
    );
    const [initialLocation, setInitialLocation] = useState<IPlace | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<IPlace | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(props.initialDate || subtractDays(today, 1));
    const [nextDateToSearch, setNextDateToSearch] = useState<null | Date>(null);
    const [initNextDateSearch, setInitNextDateToSearch] = useState<boolean>(false);
    const [showConfirmSearchModal, setShowConfirmModalSearch]= useState<boolean>(false);
    const [showLimitsReachedModal, setShowLimitsReachedModal]= useState<boolean>(false);
    const [hardLimit, setHardLimit] = useState<IHardSearchLimits | null>(null)

    useEffect(() => {
       const searchParamFromUrl: IBaseSearchParam | null = parseSearchParametersFromUrl(window.location.search);

       if (searchParamFromUrl !== null) {
           const locationFromUrl: IPlace | null = generatePlaceObject(searchParamFromUrl);
           setInitialLocation(locationFromUrl);
           setSelectedLocation(locationFromUrl);
           setSelectedDate(new Date(searchParamFromUrl.date));
       }

       getHardLimits(user.token)
            .then((result) => {
                setHardLimit(result);
            });
    }, []);

    useEffect(() => {
        if (
            staticLocation !== null &&
            staticLocation.latitude !== null &&
            staticLocation.longitude !== null &&
            nextDateToSearch !== null &&
            initNextDateSearch === true
        ) {
            const param: IBaseSearchParam = {
                date: formatDate(nextDateToSearch, 'yyyy-MM-dd'),
                longitude: staticLocation.longitude,
                latitude: staticLocation.latitude,
                location: staticLocation.name,
            };

            if (limitedBy !== null) {
                param.vehicleType = limitedBy.type;
            }

            createSearchReference(env, param)
                .then((response) => {
                    setInitNextDateToSearch(false);
                    checkSearchReferenceStatus(env, response.reference)
                        .then((searchState) => {
                            if (searchState.state === SearchState.STATE_CAR_EXPORTED) {
                                fetchResult(env, response.reference)
                                    .then((result) => {
                                        saveResultsToStorage(
                                            response.reference,
                                            result,
                                            param
                                        );
                                        history.push(`${APP_PATH}result/${response.reference}`);
                                    });
                            } else {
                                // push to search queue
                                setQueue({
                                    ref: response.reference,
                                    searchState: searchState.state,
                                    parameters: param,
                                    locationName: staticLocation.name,
                                    isStarted: false
                                });
                            }
                        });
                })
                .catch(() => {
                    setInitNextDateToSearch(false);
                });
        }
    }, [env, staticLocation, nextDateToSearch, initNextDateSearch, history, setQueue])

    const onLocationSelected = (result: IPlace | null) => setSelectedLocation(result);
    const enableMapView = typeof props.initialDate !== 'undefined';

    const search = (event: React.SyntheticEvent) => {
        event.preventDefault();
        // @Todo validation
        if (
            selectedLocation === null ||
            selectedLocation.latitude === null ||
            selectedLocation.longitude === null ||
            selectedDate === null) {
            return;
        }

        if (
            hardLimit !== null && 
            hardLimit.data.monthly_searches > hardLimit.data.hard_limit ) {
            setShowLimitsReachedModal(true);
            return;
        }
        
        setIsLoading(true);

        createSearchReference(env, {
            date: formatDate(selectedDate, 'yyyy-MM-dd'),
            longitude: selectedLocation.longitude,
            latitude: selectedLocation.latitude,
            location: selectedLocation.name
        })
            .then((result: ISearchResponse) => {
                setIsLoading(false);
                history.push(`${APP_PATH}search/${result.reference}`);
            })
            .catch((err) => {
                setIsLoading(false);
                // @Todo handle error
                console.log(err);
            })
    }

    const openMap = () => {
       if (typeof setMapView !== 'undefined') {
           setMapView(!isMapViewOpen);
       }
    }

    const searchNextPrevDay = (selected: Date) => {
        if (
            enableMapView !== true ||
            staticLocation === null ||
            staticLocation.longitude === null ||
            staticLocation.latitude === null
        ) {
            return;
        }

        findResultInStorage(selected, staticLocation.latitude, staticLocation.longitude)
            .then((results) => {
                history.push(`${APP_PATH}result/${results.ref}`);
            })
            .catch(() => {
               if (
                    hardLimit !== null && 
                    hardLimit.data.monthly_searches > hardLimit.data.hard_limit ) {
                    setShowLimitsReachedModal(true);
                    return;
               }
               setNextDateToSearch(selected);
               setShowConfirmModalSearch(true);
            });
    }

    return (
        <React.Fragment>
            <div className={`rates-search-form relative flex ${typeof props.className === 'string' ? props.className : 'mb-5'}`}>
                {
                    isLoading &&
                    <div className="bg-white flex items-center justify-center w-full h-full absolute z-10 rounded bg-opacity-50">
                        <CircularProgress/>
                    </div>
                }
                <div className="w-5/12 px-2 flex flex-col">
                    <span className="text-white text-xs font-bold mb-1 block">LOCATION</span>
                    <LocationInput
                        staticLocation={staticLocation}
                        onSelected={onLocationSelected}
                        initialLocation={initialLocation}
                    />
                </div>
                <div className={`${isMapViewOpen !== true ? 'w-5/12' : 'w-7/12'} flex`}>
                    <div className={`${enableMapView === true ? 'w-5/6' : 'w-full'} px-2 flex flex-col`}>
                        <span className="text-white text-xs font-bold mb-1 block">START DATE</span>
                        <StartDatePicker
                            enableDateNav={enableMapView === true}
                            onSelected={setSelectedDate}
                            selectedDate={selectedDate}
                            maxDate={subtractDays(today, 1)}
                            onDateNavSelected={searchNextPrevDay}
                        />
                    </div>
                </div>
                {
                    enableMapView === true &&
                    isMapViewOpen === false &&
                    <div className="w-2/12 px-2 flex flex-col justify-end">
                        <Button
                            onClick={() => { openMap() }}
                            text="Map View"
                            bg="bg-blue-200 p-2"
                            textStyle="text-blue font-bold text-center"
                            width="w-full"
                            justify="justify-between"
                        >
                            <SvgIcon height="20" width="20" viewBox="0 0 20 20" className="fill-blue inline-block">
                                <MapIcon />
                            </SvgIcon>
                            <span>View on map</span>
                            <SvgIcon className="fill-blue inline-block transform -rotate-90 -mr-2">
                                <ArrowDown />
                            </SvgIcon>
                        </Button>
                    </div>
                }
                {
                    enableMapView !== true &&
                    <div className="w-2/12 px-2 flex flex-col justify-end">
                        <Button onClick={search}
                                rounded="rounded shadow shadow-2xl"
                                textStyle="text-white font-bold"
                                width="w-full"
                                text="Search Rates"
                        />
                    </div>
                }
            </div>
            {
                showConfirmSearchModal === true &&
                nextDateToSearch !== null &&
                staticLocation !== null &&
                <ConfirmSearchModal
                    hide={() => setShowConfirmModalSearch(false)}
                    placeName={staticLocation.name}
                    selectedDate={nextDateToSearch}
                    onConfirm={() => setInitNextDateToSearch(true)}
                />
            }
            {
                showLimitsReachedModal === true &&
                <WarningModal
                    hide={() => setShowLimitsReachedModal(false)}
                    header="Oops! You have reached your maximum search limit."
                    content={`Your team has ${hardLimit?.data.hard_limit} searches a month. To increase your searches, please contact us.`}
                />
            }
        </React.Fragment>
    )
}

export default RatesSearchForm;
